import { Button, ButtonProps } from '@compass/components'
import { Menu, MenuItem } from '@compass/Menu/Menu'
import { Popover } from '@compass/Popover'
import clsx from 'clsx'
import { useCurrentSubscription, useCurrentSubscriptionData } from 'features/subscription'
import { useToast } from 'hooks'
import { canViewAnalyticsPermission, useWorkspacePermission, viewAnalyticsPermissionTooltip } from 'hooks/usePermission'
import { Download, Ellipsis, Link } from 'lucide-react'
import { SubscriptionStatus, SubscriptionType } from 'models'
import { ReactNode, useState } from 'react'
import { MenuTrigger } from 'react-aria-components'

import ExportIdentificationEventsModal from '../ExportEvent/ExportIdentificationEventsModal'

type ActionItem = {
  label: string
  isDisabled?: boolean
  onPress: () => void
  icon: ReactNode
  className?: string
  variant?: ButtonProps['variant']
  shouldDisplay?: boolean
  tooltip?: ReactNode
}

type ActionsMenuProps = {
  isExportDisabled: boolean
}

export function ActionsMenu({ isExportDisabled }: ActionsMenuProps) {
  const [exportEventsModalOpen, setExportEventsModalOpen] = useState(false)
  const { showToast } = useToast()
  const { subscription } = useCurrentSubscriptionData()

  const isPocWithRestrictedStatus =
    subscription?.status === SubscriptionStatus.Restricted && subscription?.type === SubscriptionType.ProofOfConcept
  const isExportEnabled =
    !!subscription &&
    subscription.featureSettings?.visitsExport !== false &&
    (EXPORT_ENABLED_FOR_STATUSES.includes(subscription.status) || isPocWithRestrictedStatus)

  const { currentSubscriptionId: workspaceId } = useCurrentSubscription()
  const hasPermissionToExport = useWorkspacePermission(canViewAnalyticsPermission, workspaceId)

  const actions: ActionItem[] = [
    {
      label: 'Export',
      isDisabled: isExportDisabled || !hasPermissionToExport,
      onPress: () => setExportEventsModalOpen(true),
      icon: <Download size={16} />,
      shouldDisplay: isExportEnabled,
      tooltip: hasPermissionToExport
        ? 'Export identification events as a .csv file including any filters currently applied'
        : viewAnalyticsPermissionTooltip,
    },
    {
      label: 'Copy link',
      onPress: () => {
        navigator.clipboard.writeText(document.location.href).then(
          function () {
            showToast({ severity: 'success', message: 'URL copied to clipboard!' })
          },
          function () {
            showToast({ severity: 'error', message: 'Failed to copy URL to clipboard...' })
          }
        )
      },
      icon: <Link size={16} />,
      tooltip: 'Copy the URL with filters applied. Only your team can access this link.',
      shouldDisplay: true,
    },
  ]

  return (
    <>
      <MenuTrigger>
        <Button className='md:hidden' isIcon variant='secondary' aria-label='Show identification visits actions'>
          <Ellipsis />
        </Button>
        <Popover placement='bottom end' className='w-fit min-w-[150px]'>
          <Menu>
            {actions.map(
              ({ label, icon, onPress, shouldDisplay }) =>
                shouldDisplay && (
                  <MenuItem key={label} aria-label={label} prefix={icon} className='' onAction={onPress}>
                    {label}
                  </MenuItem>
                )
            )}
          </Menu>
        </Popover>
      </MenuTrigger>
      {actions.map((action) => (
        <ActionMenuItem key={action.label} {...action} className='hidden md:flex' />
      ))}

      <ExportIdentificationEventsModal
        isOpen={exportEventsModalOpen}
        handleClose={() => setExportEventsModalOpen(false)}
      />
    </>
  )
}

function ActionMenuItem({
  label,
  variant = 'secondary',
  isDisabled,
  onPress,
  icon,
  className,
  tooltip,
  shouldDisplay,
}: ActionItem) {
  if (!shouldDisplay) {
    return null
  }

  return (
    <Button
      variant={variant}
      isDisabled={isDisabled}
      aria-label={label}
      onPress={onPress}
      tooltip={tooltip}
      className={clsx('items-center', className)}
    >
      {icon}
      {label}
    </Button>
  )
}

const EXPORT_ENABLED_FOR_STATUSES = [
  SubscriptionStatus.Trialing,
  SubscriptionStatus.Active,
  SubscriptionStatus.ProofOfConcept,
]
